import React from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import CustomTable from '../../components/CustomTable';
import CellaTesto from './CellaTesto';
import { RAGIONE_NON_REGISTRATA, descriviIstante } from './rimappatureUtils';

const useStyles = makeStyles({
  cella: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  sonda: {
    fontFamily: 'monospace',
  },
  // The reason is free text up to 1024 characters. `rt-td` forces
  // `white-space: nowrap`, so without `normal` it is cut at the column edge —
  // and the reason is the only thing this table exists for.
  ragione: {
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    maxHeight: 50,
    overflowY: 'auto',
    width: '100%',
    textAlign: 'left',
  },
});

/**
 * The voci archived in this session, with the revision that closed them: the
 * written reason, who wrote it and when.
 *
 * Session-scoped on purpose, and rendered only when it has rows (see the caller):
 * the service exposes no read route for reviewed voci
 * (`RevisioneRimappatureService` has only `vociAperte`, `contaVociAperte` and
 * `archivia`), so an empty instance of this table after a reload would assert
 * that nothing was ever archived — worse than no table at all. The trace and its
 * revision are append-only server-side: nothing is lost, it is a missing read.
 */
const VociArchiviateTable = ({ voci }) => {
  const classes = useStyles();

  const columns = [
    {
      Header: 'Sonda',
      id: 'sonda',
      minWidth: 150,
      accessor: (riga) => riga.sn,
      Cell: ({ original }) => (
        <CellaTesto testo={original.sn} className={classes.sonda} />
      ),
    },
    {
      Header: 'Provenienza',
      id: 'provenienza',
      minWidth: 260,
      accessor: (riga) =>
        `${riga.attrezzaturaEtichetta} — ${riga.controlloEtichetta}`,
      Cell: ({ original }) => (
        <CellaTesto
          testo={`${original.attrezzaturaEtichetta} — ${original.controlloEtichetta}`}
        />
      ),
    },
    {
      Header: 'Ragione',
      id: 'ragione',
      minWidth: 280,
      accessor: (riga) => riga.revisione?.ragione ?? RAGIONE_NON_REGISTRATA,
      Cell: ({ original }) => (
        <div className={classes.cella}>
          <Typography
            variant="body2"
            component="div"
            className={classes.ragione}
            tabIndex={0}
            aria-label={`Ragione dell'archiviazione della sonda ${original.sn}`}
          >
            {original.revisione?.ragione ?? RAGIONE_NON_REGISTRATA}
          </Typography>
        </div>
      ),
    },
    {
      Header: 'Archiviata da',
      id: 'chi',
      width: 170,
      accessor: (riga) => riga.revisione?.revisionataDa ?? '',
      Cell: ({ original }) => (
        <CellaTesto testo={original.revisione?.revisionataDa ?? ''} />
      ),
    },
    {
      Header: 'Archiviata il',
      id: 'quando',
      width: 160,
      accessor: (riga) => riga.revisione?.revisionataIl ?? '',
      Cell: ({ original }) => (
        <CellaTesto
          testo={descriviIstante(original.revisione?.revisionataIl)}
        />
      ),
    },
  ];

  return (
    <CustomTable
      records={voci}
      columns={columns}
      loading={false}
      filterable={false}
      resizable={true}
      sortable={true}
      defaultPageSize={5}
    />
  );
};

VociArchiviateTable.propTypes = {
  voci: PropTypes.array.isRequired,
};

export default VociArchiviateTable;
